import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScoreCard } from '@/components/ScoreCard';
import { RecommendationsList } from '@/components/RecommendationsList';
import { AuditFormData, AuditResult } from '@/lib/schemas';
import { getAuditApiUrl } from '@/lib/config';
import { trackAuditResultsView, trackBackToHome, trackGenerateReport, trackDownloadReport, trackEmailReport } from '@/lib/analytics';
import { ArrowLeft, FileText, Send, CalendarDays, Award } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import CalComCalendar from '@/components/CalComCalendar';

export default function ResultPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AuditFormData | null>(null);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);

  useEffect(() => {
    // Always ensure page starts at top on load
    window.scrollTo({ top: 0, behavior: 'auto' });

    // Get data from sessionStorage
    const storedFormData = sessionStorage.getItem('auditData');
    const storedResult = sessionStorage.getItem('auditResult');

    if (!storedFormData || !storedResult) {
      // Redirect to audit page if no data found
      navigate('/audit');
      return;
    }

    try {
      const parsedFormData = JSON.parse(storedFormData);
      const parsedResult = JSON.parse(storedResult);
      
      setFormData(parsedFormData);
      setResult(parsedResult);
      
      // Track audit results page view
      trackAuditResultsView(parsedFormData.company_name, parsedResult.score);
    } catch (error) {
      console.error('Error parsing stored data:', error);
      navigate('/audit');
    }
  }, [navigate]);

  const handleDownloadPDF = async () => {
    if (!formData || !result) return;

    // Track the download request
    trackGenerateReport(formData.company_name, result.score);
    setIsDownloading(true);
    
    try {
      const response = await fetch(getAuditApiUrl.pdf(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_data: formData,
          result,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-report-${formData.company_name.replace(/[^a-z0-9]/gi, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Track successful download
      trackDownloadReport(formData.company_name, result.score);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download PDF. Please try again.');
      console.error('PDF download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!formData || !result) return;

    // Track the email request
    trackEmailReport(formData.company_name, result.score);
    setIsEmailing(true);
    
    try {
      const response = await fetch(getAuditApiUrl.email(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_data: formData,
          result,
        }),
      });

      if (response.ok) {
        toast.success('Report sent successfully! Check your email.');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email. Please try again.');
      console.error('Email sending error:', error);
    } finally {
      setIsEmailing(false);
    }
  };

  // Show loading state while data is being loaded
  if (!formData || !result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-900 mb-1">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                <Award className="h-6 w-6" />
              </span>
              <span>Your Audit Results</span>
            </h1>
            
          </div>
          <Link to="/">
            <Button 
              size="sm" 
              className="text-white bg-[rgb(244,119,37)] hover:bg-[rgba(244,119,37,0.9)] transition-colors duration-200"
              onClick={() => trackBackToHome(formData?.company_name, result?.score)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="font-medium">Back to Home</span>
            </Button>
          </Link>
        </div>

        <ScoreCard result={result} companyName={formData.company_name} />

        <div className="mt-12 mb-12 rounded-xl bg-white/90 backdrop-blur-sm border border-orange-100 shadow-2xl">
          <div className="px-6 sm:px-10 py-10 space-y-6 text-center">
            <div className="space-y-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center justify-center gap-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                  <CalendarDays className="h-6 w-6" />
                </span>
                <span>Pick a time (20 mins)</span>
              </h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                Lock in a time that suits you and we'll review your score, identify two fast wins, and map the next steps. Zero sales pressure—just tailored guidance.
              </p>
            </div>
            <div className="w-full overflow-hidden rounded-3xl border border-gray-100 shadow-lg">
              <CalComCalendar />
            </div>
          </div>
        </div>

        <RecommendationsList result={result} recommendations={result.recommendations} />

        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">         
            <Button 
              id="btn_download_pdf_report"
              onClick={handleDownloadPDF} 
              disabled={isDownloading}
              variant="outline"
              size="lg"
              className="w-full sm:w-auto border-brand-orange/30 text-brand-orange bg-gradient-to-br from-orange-50 to-orange-100 hover:bg-gradient-to-br hover:from-brand-orange hover:to-brand-orange/90 hover:text-white hover:border-brand-orange px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <FileText className="mr-2 h-5 w-5" />
              {isDownloading ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
           
          <Button
            id="btn_email_report"
            onClick={handleSendEmail} 
            disabled={isEmailing}
            variant="outline"
            size="lg"
            className="border-brand-orange/30 text-brand-orange bg-gradient-to-br from-orange-50 to-orange-100 hover:bg-gradient-to-br hover:from-brand-orange hover:to-brand-orange/90 hover:text-white hover:border-brand-orange px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Send className="mr-2 h-5 w-5" />
            {isEmailing ? 'Sending...' : 'Email My Report'}
          </Button>
        </div>
        <div className="justify-center gap-4 mt-12">
            <Button
              id="btn_book_walkthrough_bottom"
              asChild
              size="lg"
              className="mx-auto flex items-center justify-center gap-2 rounded-xl px-8 py-3 text-white font-semibold bg-[#34a853] hover:bg-[#34a853] shadow-[0_14px_30px_-12px_rgba(52,168,83,0.65)] transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#60c145]/70 focus-visible:ring-offset-2 border border-white/20"
            >
              <a
                href="https://cal.com/advissoftware/digitalisation-audit-consultation"
                target="_blank"
                rel="noopener noreferrer"
              >
                <CalendarDays className="h-5 w-5" />
                <span>Book my free 20-mins walkthrough</span>
              </a>
            </Button>
            <p className="text-sm text-gray-500 text-center mt-3">Free • 20 minutes • No obligation</p>
          </div>

        {/* Company recap */}
        <div className="mt-12 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border-0 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-black">
            Assessment Summary
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-gray-600">
            <div>
              <strong className="text-brand-dark">Contact:</strong> {formData.contact_name}
            </div>
            <div>
              <strong className="text-brand-dark">Industry:</strong> {formData.industry}
            </div>
            <div>
              <strong className="text-brand-dark">Current Systems:</strong> {formData.current_systems}
            </div>
            <div>
              <strong className="text-brand-dark">Assessment Date:</strong> {new Date().toLocaleDateString()}
            </div>
          </div>
          {formData.top_pain_points && (
            <div className="mt-4">
              <strong className="text-brand-dark">Top Pain Points:</strong>
              <p className="text-brand-gray mt-2 leading-relaxed">{formData.top_pain_points}</p>
            </div>
          )}
        </div>

        <div className="mt-8 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border-0 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-black">
            FAQ
          </h3>
          <div className="space-y-4 text-gray-600">
            <div>
              <p className="font-semibold text-brand-dark">Q: What happens in the 20-mins call?</p>
              <p className="mt-1 text-brand-gray">A: We review your score, pick 2 quick wins, and outline next steps.</p>
            </div>
            <div>
              <p className="font-semibold text-brand-dark">Q: Is this tool-agnostic?</p>
              <p className="mt-1 text-brand-gray">A: Yes. We work with your current systems; no forced platform switch.</p>
            </div>
            <div>
              <p className="font-semibold text-brand-dark">Q: Is there a sales pitch?</p>
              <p className="mt-1 text-brand-gray">A: No. You leave with an action plan; implementation help is optional.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
